import React from "react";
import { Grid, MenuItem, TextField } from "@mui/material";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { dataFieldsArray, identificationTypes } from "../../../components/forms/complain/config";
import { visibilityStates } from "../../../app/config";
import { format } from "../../../utils/timeZoneUtils";

function ComplainDataForm({ onUpdate, model, errors, onFieldChange, complainTypes }) {
    const { t } = useTranslation('ComplainDetailsScreen', { keyPrefix: 'DataForm' });

    const [currentEdit, setCurrentEdit] = React.useState('');

    const checkIfDisabled = (field) => field !== currentEdit;
    const getErrorMessage = (field) => _.find(errors, (error) => error.field === field);

    const handleUpdate = async (name, value) => {
        if (onUpdate) {
            await onUpdate(name, value);
        }
        setCurrentEdit(null);
    }

    if (!model) {
        return null;
    }
    
    return (
        <div className="fk-form-section">
            <h3 className="title">
                {t('formTitle')}
            </h3>
            <Grid container spacing={2} style={{marginTop: 12}}>
                <Grid container spacing={2}>
                    {/* Field: id */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="id">{`${t('id')}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('id') })}>
                            <TextField
                                fullWidth
                                disabled={checkIfDisabled('id')}
                                className="fk-input"
                                placeholder={t('idPlaceholder')}
                                helperText={getErrorMessage('id')?.error}
                                value={model?.id || ''}
                                onChange={(event) => onFieldChange && onFieldChange('id', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1} />
                    </React.Fragment>
                
                    {/* Field: firstCreatedAt */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="firstCreatedAt">{`${t('firstCreatedAt')}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('firstCreatedAt') })}>
                            <TextField
                                fullWidth
                                disabled={checkIfDisabled('firstCreatedAt')}
                                className="fk-input"
                                placeholder={t('firstCreatedAtPlaceholder')}
                                helperText={getErrorMessage('firstCreatedAt')?.error}
                                value={model?.firstCreatedAt ? format(model.firstCreatedAt) : ''}
                                onChange={(event) => onFieldChange && onFieldChange('firstCreatedAt', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1} />
                    </React.Fragment>
                
                    {/* Field: date */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="date">{`${t('date')}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('date') })}>
                            <TextField
                                fullWidth
                                disabled={checkIfDisabled('date')}
                                className="fk-input"
                                placeholder={t('datePlaceholder')}
                                helperText={getErrorMessage('date')?.error}
                                value={model?.date ? format(model.date) : ''}
                                onChange={(event) => onFieldChange && onFieldChange('date', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1} />
                    </React.Fragment>
                
                    {/* Field: identyficationType */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="identyficationType">{`${t('identyficationType')}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('identyficationType') })}>
                            <TextField
                                fullWidth
                                select
                                disabled={checkIfDisabled('identyficationType')}
                                className="fk-input"
                                placeholder={t('identyficationTypePlaceholder')}
                                helperText={getErrorMessage('identyficationType')?.error}
                                value={model?.identyficationType ?? ''}
                                onChange={(event) => onFieldChange && onFieldChange('identyficationType', event.target.value)}
                            >
                                {identificationTypes.map((item, index) => (
                                    <MenuItem key={item.value || index} value={item.value}>
                                        {t(item.text)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        
                    </React.Fragment>
                
                    {/* Field: identyfication */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="identyfication">{`${t('identyfication')}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('identyfication') })}>
                            <TextField
                                fullWidth
                                disabled={checkIfDisabled('identyfication')}
                                className="fk-input"
                                placeholder={t('identyficationPlaceholder')}
                                helperText={getErrorMessage('identyfication')?.error}
                                value={model?.identyfication || ''}
                                onChange={(event) => onFieldChange && onFieldChange('identyfication', event.target.value)}
                            />
                        </Grid>
                        
                    </React.Fragment>
                
                    {/* Field: typeId */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="typeId">{`${t('typeId')}${'*'}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('typeId') })}>
                            <TextField
                                fullWidth
                                select
                                disabled={checkIfDisabled('typeId')}
                                className="fk-input"
                                placeholder={t('typeIdPlaceholder')}
                                helperText={getErrorMessage('typeId')?.error}
                                value={model?.typeId ?? ''}
                                onChange={(event) => onFieldChange && onFieldChange('typeId', event.target.value)}
                            >
                                {(complainTypes || []).map((item, index) => (
                                    <MenuItem key={item.value || index} value={item.value}>
                                        {item.text}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={1}>
                            { 'typeId' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('typeId')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('TypeId', model.typeId)}><SaveIcon /></button>
                            }
                        </Grid>
                    </React.Fragment>
                
                    {/* Field: isPublic */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="isPublic">{`${t('isPublic')}${'*'}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('isPublic') })}>
                            <TextField
                                fullWidth
                                select
                                disabled={checkIfDisabled('isPublic')}
                                className="fk-input"
                                placeholder={t('isPublicPlaceholder')}
                                helperText={getErrorMessage('isPublic')?.error}
                                value={model?.isPublic ?? ''}
                                onChange={(event) => onFieldChange && onFieldChange('isPublic', event.target.value)}
                            >
                                {visibilityStates.map((item, index) => (
                                    <MenuItem key={item.value || index} value={item.value}>
                                        {t(item.text)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item xs={1}>
                            { 'isPublic' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('isPublic')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('IsPublic', model.isPublic)}><SaveIcon /></button>
                            }
                        </Grid>
                    </React.Fragment>
                
                    {/* Field: title */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="title">{`${t('title')}${'*'}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('title') })}>
                            <TextField
                                fullWidth
                                disabled={checkIfDisabled('title')}
                                className="fk-input"
                                placeholder={t('titlePlaceholder')}
                                helperText={getErrorMessage('title')?.error}
                                value={model?.title || ''}
                                onChange={(event) => onFieldChange && onFieldChange('title', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            { 'title' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('title')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('Title', model.title)}><SaveIcon /></button>
                            }
                        </Grid>
                    </React.Fragment>
                
                    {/* Field: description */}
                    <React.Fragment>
                        <Grid item xs={3} className="fk-label">
                            <label htmlFor="description">{`${t('description')}`}</label>
                        </Grid>
                        <Grid item xs={8} className={classNames('fk-input-container', { 'has-error': !!getErrorMessage('description') })}>
                            <TextField
                                fullWidth
                                multiline
                                rows={5}
                                disabled={checkIfDisabled('description')}
                                className="fk-input"
                                placeholder={t('descriptionPlaceholder')}
                                helperText={getErrorMessage('description')?.error}
                                value={model?.description?.replaceAll('<br>','\n') || ''}
                                onChange={(event) => onFieldChange && onFieldChange('description', event.target.value)}
                            />
                        </Grid>
                        <Grid item xs={1}>
                            { 'description' !== currentEdit
                                ? <button className="fk-edit-button" onClick={() => setCurrentEdit('description')}><EditIcon /></button>
                                : <button className="fk-edit-button" onClick={() => handleUpdate('Description', model.description)}><SaveIcon /></button>
                            }
                        </Grid>
                    </React.Fragment>
                </Grid>            </Grid>
        </div>
    );
}

export default ComplainDataForm;
